import type { Identifier, RaRecord } from "react-admin";

import { navigateToBaseRoute, parsePathname } from "@/utils/route-utility";

export type RedirectToProjectDashboardOptions = {
  routeProjectId: string | null;
  queryProjectId?: string | null;
  /**
   * Where to read the project id from the create response when pathname/query did not carry it.
   * Use `"id"` for top-level `projects` create; default `"projectId"` for child resources.
   */
  projectIdField?: "projectId" | "id";
  fallback?: string;
  /**
   * Where to land after a successful create when the project id is known.
   * `"dashboard"` (default) → module home (`#/`);
   * `"resource-show"` → react-admin show for the created record.
   */
  destination?: "dashboard" | "resource-show";
};

/**
 * Redirect target for Create: in-project module dashboard (`#/`), with pathname `/projects/:projectId/`.
 * If the browser pathname does not yet include that project id, updates it via {@link navigateToBaseRoute}.
 */
export function redirectToProjectModuleDashboard(
  options: RedirectToProjectDashboardOptions
): (resource?: string, id?: Identifier, data?: Partial<RaRecord>) => string {
  const {
    routeProjectId,
    queryProjectId,
    projectIdField = "projectId",
    fallback = "list",
    destination = "dashboard",
  } = options;

  return (_resource, id, data) => {
    const fromRoute = routeProjectId?.trim() || "";
    const fromQuery = typeof queryProjectId === "string" ? queryProjectId.trim() : "";

    let fromData = "";
    if (projectIdField === "id") {
      const raw = data?.id;
      fromData = typeof raw === "string" ? raw.trim() : raw != null ? String(raw).trim() : "";
    } else {
      const raw = data?.projectId;
      fromData = typeof raw === "string" ? raw.trim() : raw != null ? String(raw).trim() : "";
    }

    const projectId = fromRoute || fromQuery || fromData;
    if (!projectId) {
      return fallback;
    }

    const pathnameId = parsePathname().routeId;
    if (pathnameId !== projectId) {
      navigateToBaseRoute("projects", projectId);
    }
    if (destination === "resource-show" && id != null) {
      return "show";
    }
    return "/";
  };
}
